"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import Container from "@/components/ui/Container";
import Logo from "@/components/ui/Logo";
import NewsletterForm from "@/components/forms/NewsletterForm";
import { SOCIAL_LINKS } from "@/lib/site-config";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";

const footerLinks = {
  Products: [
    { label: "Node as a Service", href: "/solutions/node-as-a-service" },
    { label: "RPC Endpoints", href: "/solutions/rpc-api" },
    { label: "Rollup as a Service", href: "/solutions/rollup-as-a-service" },
    { label: "Validator Nodes", href: "/solutions/validator-nodes" },
    { label: "Appchain Builder", href: "/solutions/appchain" },
    { label: "Dedicated Clusters", href: "/solutions/dedicated-clusters" },
    { label: "Enterprise Blockchain", href: "/solutions/enterprise-blockchain" },
    // { label: "For Developers", href: "/" },
    // { label: "For Enterprise & Institutions", href: "/" },
  ],
  Ecosystems: [
    { label: "Ethereum", href: "/chains/ethereum" },
    { label: "Solana", href: "/chains/solana" },
    { label: "Base", href: "/chains/base-rpc-nodes" },
    { label: "Avalanche", href: "/chains/avalanche-rpc-node" },
    { label: "BNB Smart Chain", href: "/chains/bsc-nodes" },
    { label: "Cosmos", href: "/chains/cosmos" },
    { label: "View All 50+ Chains", href: "/chains-ecosystem" },
  ],

  "Resources & Trust": [
    // { label: "Docs", href: "/" },
    { label: "Blogs & Guides", href: "/blogs" },
    // { label: "Case Studies", href: "/" },
    // { label: "System Status (live)", href: "/" },
    // { label: "Security & Compilance", href: "/" },
    { label: "Pricing", href: "/pricing" },
    { label: "Docs", href: "/docs" },
    // { label: "For Retail Stakers", href: "/" },
    // { label: "Vs QuickNode", href: "/" },
    // { label: "Vs Alchemy / Infura", href: "/" },
    // { label: "Vs AllNodes", href: "/" },
  ],
  "Company & Legal": [
    { label: "About Us", href: "/about-us" },
    // { label: "Carrers", href: "/" },
    { label: "Contact Us", href: "/contact-us" },
    { label: "Refer & Earn", href: "/refer-and-earn" },
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms & Conditions", href: "/terms-and-conditions" },
    { label: "Refund Policy", href: "/refund-policy" },
  ]
};

const socialLinks = [
  {
    label: "X (Twitter)",
    href: SOCIAL_LINKS.twitter,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    label: "LinkedIn",
    href: SOCIAL_LINKS.linkedin,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    label: "GitHub",
    href: SOCIAL_LINKS.github,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
      </svg>
    ),
  },
  {
    label: "Telegram",
    href: SOCIAL_LINKS.telegram,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M23.91 3.79L20.3 20.84c-.25 1.21-.98 1.5-2 .94l-5.5-4.07-2.66 2.57c-.3.3-.55.56-1.1.56-.72 0-.6-.27-.84-.95L6.3 13.7l-5.45-1.7c-1.18-.35-1.19-1.16.26-1.75l21.26-8.2c.97-.43 1.9.24 1.53 1.73z" />
      </svg>
    ),
  },
];

interface FooterColumnProps {
  group: string;
  links: { label: string; href: string }[];
}

function FooterColumn({ group, links }: FooterColumnProps) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(true);
  const contentId = `footer-col-${group.toLowerCase().replace(/\s+/g, "-")}`;

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const id = setTimeout(() => setIsMobile(!mq.matches), 0);
    const handler = (e: MediaQueryListEvent) => setIsMobile(!e.matches);
    mq.addEventListener("change", handler);
    return () => { clearTimeout(id); mq.removeEventListener("change", handler); };
  }, []);

  return (
    <div className="footer-col group/col border-b border-border-subtle md:border-0">
      {/* Mobile: collapsible toggle. Desktop: static heading (not keyboard-interactive). */}
      <button
        type="button"
        onClick={() => { if (isMobile) setOpen((v) => !v); }}
        aria-expanded={isMobile ? open : undefined}
        aria-controls={isMobile ? contentId : undefined}
        tabIndex={isMobile ? 0 : -1}
        className="flex w-full items-center justify-between py-4 text-left md:pointer-events-none md:cursor-default md:py-0 md:mb-4 md:select-none"
      >
        <h3 className="text-xs font-semibold uppercase tracking-widest text-text-muted transition-colors duration-200 group-hover/col:text-brand-cyan">
          {group}
        </h3>
        <ChevronDown
          size={16}
          aria-hidden
          className={`text-text-muted transition-transform duration-200 md:hidden ${open ? "rotate-180" : ""}`}
        />
      </button>
      <ul
        id={contentId}
        className={`space-y-2.5 overflow-hidden transition-all duration-200 md:block md:max-h-none md:pb-0 ${
          open ? "max-h-[500px] pb-4" : "max-h-0 md:max-h-none"
        }`}
      >
        {links.map((link) => (
          <li key={link.href} className="footer-link-item">
            <Link
              href={link.href}
              className="block text-sm text-text-secondary transition-all duration-200 hover:text-white hover:translate-x-1 min-h-[36px] md:min-h-0 leading-9 md:leading-normal"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  const footerRef = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      if (!footerRef.current) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        // Brand column slides up
        gsap.from(".footer-brand", {
          autoAlpha: 0,
          y: 30,
          duration: 0.7,
          scrollTrigger: {
            trigger: footerRef.current,
            start: "top 90%",
          },
        });

        // Link columns stagger in
        ScrollTrigger.batch(".footer-col", {
          start: "top 92%",
          onEnter: (batch) => {
            gsap.from(batch, {
              autoAlpha: 0,
              y: 24,
              stagger: 0.08,
              duration: 0.6,
              ease: "power3.out",
              overwrite: true,
            });
          },
        });

        // Individual link items stagger within columns
        ScrollTrigger.batch(".footer-link-item", {
          start: "top 95%",
          onEnter: (batch) => {
            gsap.from(batch, {
              autoAlpha: 0,
              x: -8,
              stagger: 0.03,
              duration: 0.35,
              ease: "power2.out",
              overwrite: true,
            });
          },
        });

        // Social icons pop in
        gsap.from(".footer-social", {
          autoAlpha: 0,
          scale: 0.8,
          stagger: 0.06,
          duration: 0.4,
          ease: "back.out(1.5)",
          scrollTrigger: {
            trigger: ".footer-social-row",
            start: "top 95%",
          },
        });

        // Bottom bar slides up
        gsap.from(".footer-bottom", {
          autoAlpha: 0,
          y: 12,
          duration: 0.5,
          scrollTrigger: {
            trigger: ".footer-bottom",
            start: "top 98%",
          },
        });
      });
    },
    { scope: footerRef },
  );

  return (
    <footer ref={footerRef} className="border-t border-border-subtle bg-bg-surface">
      <Container>
        {/* Main footer grid */}
        <div className="grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-6">
          {/* Brand column */}
          <div className="footer-brand lg:col-span-2">
            <Link href="/" className="mb-5 inline-block">
              <Logo />
            </Link>
            <p className="mb-5 max-w-xs text-sm leading-relaxed text-text-secondary">
              Managed blockchain infrastructure across 50+ chains. Deploy nodes,
              rollups, and appchains in minutes, backed by a 99.95% uptime SLA.
            </p>

            {/* Social links */}
            <div className="footer-social-row mb-6 flex items-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="footer-social flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-white/8 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/60"
                >
                  {s.icon}
                </a>
              ))}
            </div>

            {/* Newsletter */}
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-text-muted">
              Stay updated
            </p>
            <NewsletterForm />
          </div>

          {/* Link columns (accordion on mobile, grid on md+) */}
          {Object.entries(footerLinks).map(([group, links]) => (
            <FooterColumn key={group} group={group} links={links} />
          ))}
        </div>

        {/* Bottom bar */}
        <div className="footer-bottom flex flex-col items-center justify-between gap-4 border-t border-border-subtle py-6 text-sm text-text-muted sm:flex-row">
          <span>© {new Date().getFullYear()} Instanodes. All rights reserved.</span>
          <div className="flex gap-4">
            <Link href="/privacy-policy" className="transition-colors hover:text-white">Privacy</Link>
            <Link href="/terms-and-conditions" className="transition-colors hover:text-white">Terms</Link>
            <Link href="/refund-policy" className="transition-colors hover:text-white">Refund Policy</Link>
          </div>
        </div>
      </Container>
    </footer>
  );
}
